import React from 'react';
import { CalculationResult } from '../types';
import { translations } from '../utils/translations';
import { Droplet, Beaker, Zap } from 'lucide-react';
import { formatNumber, formatCurrency } from '../utils/calculations';

interface ResultsDisplayProps {
  result: CalculationResult;
  t: typeof translations['en'];
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ result, t }) => {
  const isZero = result.totalVolume === 0;

  return (
    <div className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white rounded-3xl shadow-2xl p-8 space-y-8 relative overflow-hidden ring-1 ring-white/10">
      
      {/* Abstract Background Shapes */}
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-indigo-500 rounded-full mix-blend-overlay filter blur-[60px] opacity-40"></div>
      <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-blue-500 rounded-full mix-blend-overlay filter blur-[60px] opacity-30"></div>

      {/* Main Volume Display */}
      <div className="relative z-10 text-center">
        <h2 className="text-indigo-200 text-xs font-bold uppercase tracking-[0.2em] mb-3">{t.results.title}</h2>
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-6xl md:text-7xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-white to-indigo-100">
            {isZero ? '0' : formatNumber(result.totalVolume)}
          </span>
          <span className="text-2xl text-indigo-300 font-medium">{result.unit}</span>
        </div>
        {result.estimatedCost > 0 && !isZero && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 rounded-full border border-white/10">
            <span className="text-xs text-indigo-200">{t.results.estCost}:</span>
            <span className="text-sm font-bold text-green-300">{formatCurrency(result.estimatedCost)}</span>
          </div>
        )}
      </div>

      {/* Part A & Part B Breakdown */}
      <div className="grid grid-cols-2 gap-4 relative z-10">
        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 hover:bg-white/10 transition-colors">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-blue-500/20 rounded-lg">
              <Droplet className="w-4 h-4 text-blue-300" />
            </div>
            <span className="text-blue-100 text-sm font-semibold">{t.results.resinA}</span>
          </div>
          <div className="text-2xl font-bold">
            {isZero ? '--' : formatNumber(result.partA)} 
          </div>
          <span className="text-xs text-blue-300/60 font-medium">{result.unit}</span>
        </div>

        <div className="bg-white/5 backdrop-blur-md rounded-2xl p-5 border border-white/10 hover:bg-white/10 transition-colors">
          <div className="flex items-center gap-2 mb-3">
             <div className="p-1.5 bg-purple-500/20 rounded-lg">
              <Beaker className="w-4 h-4 text-purple-300" />
            </div>
            <span className="text-purple-100 text-sm font-semibold">{t.results.hardenerB}</span>
          </div>
          <div className="text-2xl font-bold">
             {isZero ? '--' : formatNumber(result.partB)} 
          </div>
          <span className="text-xs text-purple-300/60 font-medium">{result.unit}</span>
        </div>
      </div>

      {/* Visual Bar */}
      {!isZero && (
        <div className="relative z-10">
          <div className="w-full bg-slate-800/50 rounded-full h-4 overflow-hidden flex border border-white/5">
            <div 
              className="bg-gradient-to-r from-blue-500 to-blue-400 h-full transition-all duration-700 ease-out relative group" 
              style={{ width: `${(result.partA / result.totalVolume) * 100}%` }}
            >
              <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <div 
              className="bg-gradient-to-r from-purple-500 to-purple-400 h-full transition-all duration-700 ease-out relative group" 
              style={{ width: `${(result.partB / result.totalVolume) * 100}%` }}
            >
               <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-2 px-1 uppercase font-bold tracking-wider">
            <span>{t.results.partA}</span>
            <span>{t.results.partB}</span>
          </div>
        </div>
      )}

      {/* Action Hint */}
      {isZero && (
        <div className="relative z-10 text-center pb-2">
           <div className="inline-flex items-center gap-2 text-indigo-300/50 text-sm">
             <Zap className="w-4 h-4" />
             <span>{t.results.emptyState}</span>
           </div>
        </div>
      )}
    </div>
  );
};

export default ResultsDisplay;